import React, { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { apiErrorCode, friendlyCode } from '../../lib/friendlyCodes';

const hm = (iso) => (iso ? String(iso).slice(11, 16) : '—');

// Day 23 DEF-23-07 — participant attendance (recorded by the worker) needs an approver; the backend existed, the UI did not.
export const ShiftAttendanceApprovalPanel = ({ shift, permissions = [], onChanged }) => {
  const canApprove = permissions.includes('timesheets.approve');
  const shiftId = shift.id || shift._id;
  const [rows, setRows] = useState([]);
  const [names, setNames] = useState({});
  const load = useCallback(async () => {
    try { const r = await api.get(`/shifts/${shiftId}/attendance`); setRows(r.data?.data || []); } catch (e) { setRows([]); }
  }, [shiftId]);
  useEffect(() => { if (canApprove) load(); }, [load, canApprove]);
  useEffect(() => {
    let alive = true;
    Promise.all([...new Set(rows.map((r) => String(r.participant_id)))].map(async (p) => { try { const r = await api.get(`/participants/${p}`); const d = r.data?.data || r.data; return [p, `${d.first_name || ''} ${d.last_name || ''}`.trim()]; } catch (e) { return [p, null]; } }))
      .then((xs) => { if (alive) setNames(Object.fromEntries(xs.filter(([, n]) => n))); });
    return () => { alive = false; };
  }, [rows.map((r) => r.participant_id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canApprove || rows.length === 0) return null;
  const approve = async (r) => {
    try {
      await api.post(`/shifts/${shiftId}/attendance/${r.participant_id}/approve`, { notes: 'Approved from the Scheduling Board' });
      toast.success('Participant attendance approved'); await load(); onChanged && onChanged();
    } catch (e) { toast.error(friendlyCode(apiErrorCode(e)) || e?.response?.data?.error?.message || e?.response?.data?.detail?.message || 'Could not approve attendance'); }
  };
  return (
    <div className="rounded-lg border bg-card p-3 space-y-2" data-testid="shift-attendance-approval-panel">
      <div className="text-sm font-semibold">Participant attendance (worker-recorded) — approval</div>
      {rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between text-xs gap-2" data-testid={`attendance-row-${r.participant_id}`}>
          <span className="truncate">{names[String(r.participant_id)] || `Participant …${String(r.participant_id).slice(-4)}`} · {r.status} · {hm(r.actual_start)}–{hm(r.actual_end)}</span>
          {r.approved ? <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid={`attendance-approved-${r.participant_id}`}>Approved</Badge>
            : <Button size="sm" variant="outline" className="h-7" onClick={() => approve(r)} data-testid={`approve-attendance-${r.participant_id}`}>Approve</Button>}
        </div>
      ))}
    </div>
  );
};

export default ShiftAttendanceApprovalPanel;
